// Pure bottom-sheet rules shared by every sheet. No DOM here, so tests can drive them.
export const DRAG = {share: 0.25, minDistance: 56, velocity: 0.5, flick: 12, slop: 6, window: 100};

// Decide what a released drag does. dy > 0 means pulled down; velocity in px/ms, downwards positive.
export function releaseDecision(dy, velocity, height) {
  if (!(dy > 0)) return 'open';
  if (velocity >= DRAG.velocity && dy >= DRAG.flick) return 'close';
  if (velocity <= -DRAG.velocity) return 'open';
  return dy >= Math.max(DRAG.minDistance, height * DRAG.share) ? 'close' : 'open';
}

// A touch that starts in the sheet content: native scroll or sheet drag, like iOS/Android sheets.
// Down at scrollTop 0 pulls the sheet; content that cannot scroll is dragged directly (both ways,
// upwards as rubber band); sideways or real scrolling stays native. 'pending' until past the slop.
export function contentGesture({dy, dx = 0, scrollTop = 0, scrollable = true}) {
  if (Math.hypot(dx, dy) < DRAG.slop) return 'pending';
  if (Math.abs(dx) > Math.abs(dy)) return 'scroll';
  if (!scrollable) return 'sheet';
  return dy > 0 && scrollTop <= 0 ? 'sheet' : 'scroll';
}
// The side drawer (main menu) closes by a leftwards swipe: dx < 0 pulls it, vertical moves stay native scrolling.
export function drawerGesture({dx, dy = 0}) {
  if (Math.hypot(dx, dy) < DRAG.slop) return 'pending';
  return dx < 0 && Math.abs(dx) > Math.abs(dy) ? 'sheet' : 'scroll';
}
// Controls that own their own drag (range sliders, text fields) never move the sheet.
export const OWN_DRAG = 'input[type="range"], textarea, select, input[type="text"], input[type="search"]';

// Downwards follows the finger, upwards resists like a rubber band.
export const dragOffset = dy => dy >= 0 ? dy : -Math.min(24, Math.sqrt(-dy) * 2);

// Velocity from the last ~100 ms of pointer samples.
export function velocityTracker(windowMs = DRAG.window) {
  let samples = [];
  return {
    add(y, t) { samples.push([y, t]); while (samples.length > 2 && t - samples[0][1] > windowMs) samples.shift(); },
    velocity() {
      if (samples.length < 2) return 0;
      const [y0, t0] = samples[0], [y1, t1] = samples.at(-1);
      return t1 > t0 ? (y1 - y0) / (t1 - t0) : 0;
    },
    reset() { samples = []; },
  };
}

// closed -> opening -> open -> closing -> closed. Opening a second sheet swaps instantly.
export function sheetMachine() {
  let state = 'closed', active = null;
  return {
    get state() { return state; },
    get active() { return active; },
    open(id) {
      const swap = active && active !== id && state !== 'closed' ? active : null;
      if (active === id && (state === 'open' || state === 'opening')) return {changed: false, swap: null};
      active = id; state = 'opening';
      return {changed: true, swap};
    },
    close() {
      if (state === 'closed' || state === 'closing') return {changed: false, id: active};
      state = 'closing';
      return {changed: true, id: active};
    },
    // Called when the CSS transition ends (or immediately with reduced motion).
    settled(id) {
      if (id !== active) return state;
      if (state === 'opening') state = 'open';
      else if (state === 'closing') { state = 'closed'; active = null; }
      return state;
    },
  };
}
